import { useEffect, useState } from 'react';
import { useApp } from '../store.js';
import { index } from '../worker/client.js';
import type { TagNode } from '../worker/api.js';
import { usePaneState } from './paneState.js';
import { useIndexRev } from './hooks.js';

export function TagPane({ onSearchTag }: { onSearchTag?: (tag: string) => void }) {
  const rev = useIndexRev();
  const setSidebarTab = useApp((s) => s.setSidebarTab);
  const setSearchQuery = usePaneState((s) => s.setSearchQuery);
  const [tree, setTree] = useState<TagNode[] | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let stale = false;
    void index.tags().then((t) => {
      if (!stale) setTree(t);
    });
    return () => {
      stale = true;
    };
  }, [rev]);

  const pick = (tag: string) => {
    setSearchQuery(`tag:${tag}`);
    setSidebarTab('search');
    onSearchTag?.(tag);
  };

  const toggle = (tag: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  const renderNode = (node: TagNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsed.has(node.tag);
    const label = node.tag.split('/').pop() ?? node.tag;
    return (
      <div key={node.tag}>
        <div className="tree-row tag-row" style={{ paddingLeft: 8 + depth * 14 }}>
          {hasChildren ? (
            <button
              className={`tree-chevron btn${isCollapsed ? '' : ' open'}`}
              title={isCollapsed ? 'Expand' : 'Collapse'}
              onClick={() => toggle(node.tag)}
            >
              ▸
            </button>
          ) : (
            <span className="tree-chevron" />
          )}
          <button
            className="tag-name"
            title={`Search tag:${node.tag}`}
            onClick={() => pick(node.tag)}
          >
            #{label}
          </button>
          <span className="count-badge">{node.count}</span>
        </div>
        {hasChildren && !isCollapsed && node.children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  if (tree === null) {
    return (
      <div className="pane">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="pane">
      <div className="pane-header">
        <span className="pane-title">Tags</span>
      </div>
      {tree.length === 0 ? (
        <div className="pane-empty">
          No tags yet.
          <br />
          Add <code>#tags</code> to your notes to see them here.
        </div>
      ) : (
        <div className="tree">{tree.map((n) => renderNode(n, 0))}</div>
      )}
    </div>
  );
}
